import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function SpoedForm() {
  const { t } = useTranslation("inbraakschade-reparatie");
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    wanneer: "",
    type: "",
    voornaam: "",
    achternaam: "",
    telefoon: "",
    adres: "",
    postcode: "",
    verzekering: "",
    bericht: "",
  });

  const stepLabels = t("spoedForm.steps", { returnObjects: true }) as string[];
  const step1Options = t("spoedForm.step1.options", { returnObjects: true }) as string[];
  const step2Options = t("spoedForm.step2.options", { returnObjects: true }) as string[];

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([k, v]) => payload.append(k, v));
      payload.append("_subject", "SPOED inbraakschade aanvraag");
      const res = await fetch("https://formspree.io/f/xgoqdank", {
        method: "POST",
        body: payload,
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        setSubmitted(true);
      }
    } catch {
      /* network error */
    }
  };

  if (submitted) {
    return (
      <section id="spoedformulier" className="bg-white py-16 md:py-24 w-full">
        <div className="max-w-[700px] mx-auto px-4 md:px-10 text-center">
          <div className="w-16 h-16 rounded-full bg-brand-yellow flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-brand-text text-2xl w-8 h-8 flex items-center justify-center"></i>
          </div>
          <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text mb-4">
            {t("spoedForm.success.title")}
          </h2>
          <p className="font-geist text-base text-brand-text/70">
            {t("spoedForm.success.body")}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="spoedformulier" className="bg-white py-16 md:py-24 w-full">
      <div className="max-w-[700px] mx-auto px-4 md:px-10">
        <h2 className="font-geist font-bold text-2xl md:text-3xl lg:text-4xl text-brand-text tracking-tight text-center mb-4 md:mb-6">
          {t("spoedForm.h2")}
        </h2>

        {/* Progress */}
        <div className="flex items-center gap-3 md:gap-4 mb-10 md:mb-12">
          {stepLabels.map((label, i) => (
            <div key={i} className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center font-geist-mono text-sm font-bold ${
                    i <= step
                      ? "bg-brand-yellow text-brand-text"
                      : "bg-brand-cream text-brand-text/40"
                  }`}
                >
                  {i + 1}
                </div>
                <span
                  className={`font-geist text-sm ${
                    i <= step
                      ? "text-brand-text font-semibold"
                      : "text-brand-text/40"
                  }`}
                >
                  {label}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-brand-cream overflow-hidden">
                <div
                  className="h-full bg-brand-yellow rounded-full transition-all duration-300"
                  style={{
                    width:
                      i < step
                        ? "100%"
                        : i === step
                          ? `${((step + 1) / stepLabels.length) * 100}%`
                          : "0%",
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <form
          onSubmit={handleSubmit}
          action="https://formspree.io/f/xgoqdank"
          method="POST"
          data-readdy-form
          id="spoed-inbraakschade-reparatie"
          className="space-y-6"
        >
          <input
            type="text"
            name="_gotcha"
            style={{ display: "none" }}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          {step === 0 && (
            <div>
              <p className="font-geist font-semibold text-sm text-brand-text mb-4">
                {t("spoedForm.step1.label")}
              </p>
              <div className="space-y-2">
                {step1Options.map((opt) => (
                  <label
                    key={opt}
                    className={`flex items-center gap-3 px-4 py-4 rounded-xl border cursor-pointer transition-colors min-h-[44px] ${
                      formData.wanneer === opt
                        ? "border-brand-yellow bg-brand-yellow/10"
                        : "border-brand-border hover:border-brand-yellow/60"
                    }`}
                  >
                    <input
                      type="radio"
                      name="wanneer"
                      value={opt}
                      checked={formData.wanneer === opt}
                      onChange={(e) =>
                        handleChange("wanneer", e.target.value)
                      }
                      className="w-4 h-4 accent-brand-blue"
                    />
                    <span className="font-geist text-sm text-brand-text">
                      {opt}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="font-geist font-semibold text-sm text-brand-text mb-4">
                {t("spoedForm.step2.label")}
              </p>
              <div className="space-y-2">
                {step2Options.map((opt) => (
                  <label
                    key={opt}
                    className={`flex items-center gap-3 px-4 py-4 rounded-xl border cursor-pointer transition-colors min-h-[44px] ${
                      formData.type === opt
                        ? "border-brand-yellow bg-brand-yellow/10"
                        : "border-brand-border hover:border-brand-yellow/60"
                    }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value={opt}
                      checked={formData.type === opt}
                      onChange={(e) =>
                        handleChange("type", e.target.value)
                      }
                      className="w-4 h-4 accent-brand-blue"
                    />
                    <span className="font-geist text-sm text-brand-text">
                      {opt}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-geist text-sm font-semibold text-brand-text mb-1.5">
                    {t("spoedForm.step3.firstName")} *
                  </label>
                  <input
                    type="text"
                    name="voornaam"
                    value={formData.voornaam}
                    onChange={(e) =>
                      handleChange("voornaam", e.target.value)
                    }
                    required
                    className="w-full px-4 py-3.5 rounded-xl border border-brand-border font-geist text-base text-brand-text focus:outline-none focus:border-brand-blue transition-colors"
                    placeholder="Voornaam"
                  />
                </div>
                <div>
                  <label className="block font-geist text-sm font-semibold text-brand-text mb-1.5">
                    {t("spoedForm.step3.lastName")}
                  </label>
                  <input
                    type="text"
                    name="achternaam"
                    value={formData.achternaam}
                    onChange={(e) =>
                      handleChange("achternaam", e.target.value)
                    }
                    className="w-full px-4 py-3.5 rounded-xl border border-brand-border font-geist text-base text-brand-text focus:outline-none focus:border-brand-blue transition-colors"
                    placeholder="Achternaam"
                  />
                </div>
              </div>
              <div>
                <label className="block font-geist text-sm font-semibold text-brand-text mb-1.5">
                  {t("spoedForm.step3.phone")} *
                </label>
                <input
                  type="tel"
                  name="telefoon"
                  value={formData.telefoon}
                  onChange={(e) =>
                    handleChange("telefoon", e.target.value)
                  }
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-brand-border font-geist text-base text-brand-text focus:outline-none focus:border-brand-blue transition-colors"
                  placeholder="06 ..."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-geist text-sm font-semibold text-brand-text mb-1.5">
                    {t("spoedForm.step3.address")} *
                  </label>
                  <input
                    type="text"
                    name="adres"
                    value={formData.adres}
                    onChange={(e) =>
                      handleChange("adres", e.target.value)
                    }
                    required
                    className="w-full px-4 py-3.5 rounded-xl border border-brand-border font-geist text-base text-brand-text focus:outline-none focus:border-brand-blue transition-colors"
                    placeholder="Straat + huisnummer"
                  />
                </div>
                <div>
                  <label className="block font-geist text-sm font-semibold text-brand-text mb-1.5">
                    {t("spoedForm.step3.postcode")} *
                  </label>
                  <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={(e) =>
                      handleChange("postcode", e.target.value)
                    }
                    required
                    className="w-full px-4 py-3.5 rounded-xl border border-brand-border font-geist text-base text-brand-text focus:outline-none focus:border-brand-blue transition-colors"
                    placeholder="1234 AB"
                  />
                </div>
              </div>
              <div>
                <label className="block font-geist text-sm font-semibold text-brand-text mb-1.5">
                  {t("spoedForm.step3.insurance")}
                </label>
                <input
                  type="text"
                  name="verzekering"
                  value={formData.verzekering}
                  onChange={(e) =>
                    handleChange("verzekering", e.target.value)
                  }
                  className="w-full px-4 py-3.5 rounded-xl border border-brand-border font-geist text-base text-brand-text focus:outline-none focus:border-brand-blue transition-colors"
                  placeholder={t("spoedForm.step3.insurancePlaceholder")}
                />
              </div>
              <div>
                <label className="block font-geist text-sm font-semibold text-brand-text mb-1.5">
                  {t("spoedForm.step3.message")}
                </label>
                <textarea
                  name="bericht"
                  value={formData.bericht}
                  onChange={(e) =>
                    handleChange("bericht", e.target.value)
                  }
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3.5 rounded-xl border border-brand-border font-geist text-base text-brand-text focus:outline-none focus:border-brand-blue transition-colors resize-none"
                  placeholder={t("spoedForm.step3.messagePlaceholder")}
                ></textarea>
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between pt-4">
            {step > 0 ? (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="font-geist font-semibold text-sm text-brand-text/60 hover:text-brand-text transition-colors flex items-center gap-1 whitespace-nowrap min-h-11"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                {t("spoedForm.step3.back")}
              </button>
            ) : (
              <span></span>
            )}
            {step < 2 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="font-geist font-bold text-sm bg-brand-blue text-white px-6 py-3.5 rounded-full hover:bg-brand-text transition-colors flex items-center gap-1 whitespace-nowrap min-h-11"
              >
                {t("spoedForm.step3.next")}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            ) : (
              <button
                type="submit"
                className="font-geist font-bold text-sm bg-brand-yellow text-brand-text px-6 py-3.5 rounded-full hover:bg-brand-yellow/80 transition-colors whitespace-nowrap min-h-11"
              >
                {t("spoedForm.step3.submit")}
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}